// The browser tab's title per screen (review 2026-10-06: every tab, bookmark and history entry of
// Paige read "Paige"). One watcher on the router, like the screen views (analytics/screens.ts).
import type { QueryClient } from '@tanstack/react-query';
import type { AnyRouter } from '@tanstack/react-router';
import { keys } from '../api/queries';
import { settingsGroups } from '../screens/company/settings-topics';

const PRODUCT = 'Paige';

/** Staff screens, named as the admin area's own menu and page headers name them. */
const staff: [prefix: string, title: string][] = [
  ['/admin/today', 'This week'],
  ['/admin/funnel', 'Funnel'],
  ['/admin/campaigns', 'Campaigns'],
  ['/admin/retention', 'Retention'],
  ['/admin/usage', 'Feature use'],
  ['/admin/friction', 'Friction'],
  ['/admin/revenue', 'Revenue'],
  ['/admin/queues', 'Stages and queues'],
  ['/admin/problems', 'Problems'],
  ['/admin/timeline', 'Timeline'],
  ['/admin/pools', 'Pools and releases'],
  ['/admin/desktop-gpus', 'Desktop GPUs'],
  ['/admin/health', 'Health'],
  ['/admin/customers', 'Customer'],
];

/** The developer guides by slug, as `screens/developers/guides/registry.ts` titles them (the test holds the two together). */
export const guideTitles: Record<string, string> = {
  'processing-modes': 'Processing modes',
  results: 'Results and exports',
  webhooks: 'Webhooks',
  errors: 'Errors and retries',
  limits: 'Limits and versions',
  changelog: 'Changelog',
};

/** What the tab says where the address leads nowhere. */
const NOT_FOUND = 'Page not found';

/** The screens outside a company's settings, by route; a group's routes share its name. */
const named: [prefix: string, title: string][] = [
  ['/t/$tenant/jobs/new', 'New job'],
  ['/t/$tenant/jobs/$job/review', 'Job'],
  ['/t/$tenant/package', 'Plans'],
  ['/t/$tenant/settings', 'Settings'],
  ['/t/$tenant', 'Jobs'],
  ['/admin', 'Admin'],
  ['/developers/reference', 'API reference'],
  ['/developers', 'Developers'],
  ['/device_', 'Connect this computer'],
  ['/device', 'Allow a device'],
  ['/invitations', 'Invitations'],
  ['/m/', 'Paige Mobile'],
  ['/email/unsubscribe', 'Email choices'],
  ['/forgot-password', 'Forgot password'],
  ['/reset-password', 'New password'],
  ['/pricing', 'Pricing'],
  ['/sign-in', 'Sign in'],
  ['/sign-up', 'Sign up'],
];

const withProduct = (...parts: string[]): string => [...parts, PRODUCT].join(' · ');

/** What a route's own title can depend on besides its id. */
export interface TitleFacts {
  /** The developer guide the address names (`/developers/$guide`). */
  guide?: string | undefined;
  /** The address matched no screen: the 404. */
  missing?: boolean | undefined;
}

/** A section's own screen: "Problems · Admin · Paige", "Webhooks · Developers · Paige". */
function sectionTitle(id: string, facts: TitleFacts): string | null {
  const admin = staff.find(([prefix]) => id === prefix || id.startsWith(`${prefix}/`));
  if (admin) return withProduct(admin[1], 'Admin');
  const guide = id === '/developers/$guide' ? guideTitles[facts.guide ?? ''] : undefined;
  return guide ? withProduct(guide, 'Developers') : null;
}

/**
 * The title for a route: "Jobs · Paige", the job's own name, "Plan and credits · Billing · Paige".
 * A route with no name of its own is just "Paige"; never a library's or a vendor's word.
 */
export function titleOf(routeId: string, jobName?: string | null, facts: TitleFacts = {}): string {
  if (facts.missing) return withProduct(NOT_FOUND);
  const id = routeId.replace(/\/$/, '') || '/';
  for (const group of settingsGroups) {
    const topic = group.topics.find((t) => t.to === id);
    if (topic) return withProduct(topic.label, group.label);
  }
  if (id === '/t/$tenant/jobs/$job/review' && jobName) return withProduct(jobName);
  const own = sectionTitle(id, facts);
  if (own) return own;
  const match = named.find(([prefix]) => id === prefix || id.startsWith(prefix));
  return match ? withProduct(match[1]) : PRODUCT;
}

/** Whether the 404 is what is on screen, and the watcher's own way to say so at once. */
let missing = false;
let retitle = (): void => undefined;

/**
 * The 404 says it is showing (and that it has gone): the address that led nowhere still matched a
 * route's shape or none, so the route alone cannot name it (`shell/NotFound.tsx`).
 */
export function showingNotFound(on: boolean): void {
  missing = on;
  retitle();
}

/** Keeps `document.title` on the screen the router shows, and on a job's name once it is read. */
export function watchTitles(router: AnyRouter, queryClient: QueryClient): () => void {
  const apply = () => {
    const last = router.state.matches[router.state.matches.length - 1];
    const params = (last?.params ?? {}) as { tenant?: string; job?: string; guide?: string };
    const job =
      params.tenant && params.job
        ? queryClient.getQueryData<{ name?: string }>(keys.job(params.tenant, params.job))
        : undefined;
    const title = titleOf(String(last?.routeId ?? '/'), job?.name, {
      guide: params.guide,
      missing,
    });
    if (document.title !== title) document.title = title;
  };
  retitle = apply;
  const offRouter = router.subscribe('onResolved', apply);
  // A job renamed, or read after its route resolved, renames its tab.
  const offCache = queryClient.getQueryCache().subscribe(apply);
  apply();
  return () => {
    offRouter();
    offCache();
  };
}
